/**
 * Helper functions for making API calls
 */

import { HOST_AUTO_SERVER } from "../constants/api";
import { STORAGE_KEY } from "../constants/storageKeys";
import { getLocalStorage } from "./localStorage";

const BASE_URL = HOST_AUTO_SERVER;

/**
 * Common headers for API requests
 */
const getHeaders = () => {
  const token = getLocalStorage(STORAGE_KEY.LOGIN_TOKEN);
  const headers = {
    'Content-Type': 'application/json',
    // Add any common headers here (e.g. authentication token)
    Authorization: 'Bearer ' + token?.token
  };
  return headers;
};

/**
 * Generic error handler for API calls
 */
const handleError = (error) => {
  if (error.response) {
    // Server responded with error
    return {
      error: true,
      message: error.response.data.message || 'Server error occurred',
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      error: true,
      message: 'No response from server',
      status: 503
    };
  } else {
    // Error in request setup
    return {
      error: true,
      message: error.message || 'Error setting up request',
      status: 400
    };
  }
};

/**
 * GET request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters (optional)
 * @returns {Promise} Response data or error
 */
export const get = async (endpoint, params = {}) => {
  try {
    //console.log(endpoint)
    const queryString = new URLSearchParams(params).toString();
    const url = `${BASE_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Nếu JSON không hợp lệ, trả về object rỗng
      throw {
        response: {
          status: response.status,
          data: errorData,
        },
      };
    }

    const data = await response.json();
    return { error: false, data };

  } catch (error) {
    return handleError(error);
  }
};

/**
 * POST request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options (optional)
 * @returns {Promise} Response data or error
 */
export const post = async (endpoint, body, options = {}) => {
  try {
    const url = `${BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Nếu JSON không hợp lệ, trả về object rỗng
      throw {
        response: {
          status: response.status,
          data: errorData,
        },
      };
    }

    const data = await response.json();
    return { error: false, data };

  } catch (error) {
    return handleError(error);
  }
};

// Usage example:
/*
import { get, post } from './utils/helpers';

// GET request
const getUsers = async () => {
  const response = await get('/users', { page: 1, limit: 10 });
  if (!response.error) {
    //console.log(response.data);
  }
};

// POST request
const createUser = async (userData) => {
  const response = await post('/users', userData);
  if (!response.error) {
    //console.log(response.data);
  }
};
*/